import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import postsFallback from "@/data/posts.json";

export type PublicPost = {
  id: string;
  type: "blog" | "harmreduction";
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  published_date: string;
};

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function normalizeDbRow(r: any): PublicPost {
  return {
    id: r.legacy_id != null ? String(r.legacy_id) : String(r.id),
    type: r.type,
    title: r.title ?? "",
    slug: r.slug ?? "",
    content: r.content ?? "",
    excerpt: r.excerpt ?? "",
    published_date:
      typeof r.published_date === "string"
        ? r.published_date
        : r.published_date
        ? new Date(r.published_date).toISOString().slice(0, 10)
        : "",
  };
}

function fallbackList(type: "blog" | "harmreduction"): PublicPost[] {
  return (postsFallback as any[])
    .filter((p) => p.type === type)
    .map((p) => ({
      id: String(p.id),
      type: p.type,
      title: p.title ?? "",
      slug: p.slug ?? "",
      content: p.content ?? "",
      excerpt: p.excerpt ?? "",
      published_date: p.published_date ?? "",
    }))
    .sort((a, b) => b.published_date.localeCompare(a.published_date));
}

const typeValidator = (i: unknown) => {
  const t = (i as any)?.type;
  if (t !== "blog" && t !== "harmreduction") throw new Error("Ungueltiger type");
  return { type: t as "blog" | "harmreduction" };
};

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator(typeValidator)
  .handler(async ({ data }): Promise<PublicPost[]> => {
    try {
      const sb = publicClient();
      const { data: rows, error } = await sb
        .from("posts")
        .select("id, legacy_id, type, title, slug, content, excerpt, published_date, published")
        .eq("type", data.type)
        .eq("published", true)
        .order("published_date", { ascending: false });
      if (error) {
        console.error("[public-content] listPublishedPosts DB error", error.message);
        return fallbackList(data.type);
      }
      if (!rows || rows.length === 0) {
        return fallbackList(data.type);
      }
      return rows.map(normalizeDbRow);
    } catch (e: any) {
      console.error("[public-content] listPublishedPosts threw", e?.message ?? e);
      return fallbackList(data.type);
    }
  });

export const getPublishedPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    const t = (i as any)?.type;
    const slug = (i as any)?.slug;
    if (t !== "blog" && t !== "harmreduction") throw new Error("Ungueltiger type");
    if (typeof slug !== "string" || !slug) throw new Error("Ungueltiger slug");
    return { type: t as "blog" | "harmreduction", slug };
  })
  .handler(async ({ data }): Promise<PublicPost | null> => {
    const fromFallback = () =>
      fallbackList(data.type).find((p) => p.slug === data.slug) ?? null;
    try {
      const sb = publicClient();
      const { data: row, error } = await sb
        .from("posts")
        .select("id, legacy_id, type, title, slug, content, excerpt, published_date, published")
        .eq("type", data.type)
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle();
      if (error) {
        console.error("[public-content] getPublishedPostBySlug DB error", error.message);
        return fromFallback();
      }
      if (!row) return fromFallback();
      return normalizeDbRow(row);
    } catch (e: any) {
      console.error("[public-content] getPublishedPostBySlug threw", e?.message ?? e);
      return fromFallback();
    }
  });
